import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { gsPrimitives, haaPrimitives, typeSize, rem } from '@gbi/ui';
import config from '../../../config';
import tokens from '../../../assets/styles/tokens';
import Paragraph from '../../Paragraph/Paragraph';
import Button from '../../Button/Button';
import { ReactComponent as AlertCloseIconSvg } from '../../../assets/images/priceAlerts/alert-close-icon.svg';
import { ReactComponent as InfoGttIconSvg } from '../../../assets/images/priceAlerts/info-gtt.svg';
import { ReactComponent as SearchSvg } from '../../../assets/images/gtt/search.svg';
import { ReactComponent as CheckCircleBlueSvg } from '../../../assets/images/svg/check-circle-blue.svg';
import { ReactComponent as ArrowDownSvg } from '../../../assets/images/gtt/arrow_down.svg';
import { ReactComponent as PlusIconSvg } from '../../../assets/images/svg/quantity-plus.svg';
import { ReactComponent as MinusIconSvg } from '../../../assets/images/svg/quantity-minus.svg';
import { ReactComponent as VaultSvg } from '../../../assets/images/svg/vault.svg';
import { ReactComponent as HandCoinsSvg } from '../../../assets/images/svg/hand-coins.svg';
import { ReactComponent as DiscountSvg } from '../../../assets/images/gtt/discount.svg';
import themeColors from '../../../assets/styles/themeColors';
import {
  mediaBelow,
  mediaUp,
  pxToRem,
  pxToRemMd,
} from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import { uButtonClear, uOutlineNone } from '../../../assets/styles/utility';

// gbi/ui's slate/600 token. Same hex for both brands today, but resolved
// per-brand so it keeps tracking the design system if that ever changes.
const SLATE_600 =
  config.brokerTheme === 'GoldSilver'
    ? gsPrimitives.slate600
    : haaPrimitives.slate600;

// Row of single-select metal pills with a trailing search affordance.
export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${pxToRem(12)};
  margin-bottom: ${pxToRem(16)};
`;

export const MetalChips = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  flex: 1;
`;

// Outlined pill. All chips share a light border and dark text; the selected
// chip is distinguished by a blue border and a trailing check icon (no fill).
export const MetalChip = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
  color: ${themeColors.colorPrimary};
  background-color: transparent;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 500)};
  font-size: 14px;
  line-height: 1.25;
  padding: ${pxToRem(8)} ${pxToRem(16)};
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? themeColors.colorPrimary : themeColors.colorBorderPrimary};
  border-radius: ${variables.borderRadius.borderRadius};
  cursor: pointer;

  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

export const MetalChipCheck = styled(CheckCircleBlueSvg)`
  &&& {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

export const SearchToggle = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${themeColors.colorTextPrimary};
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
`;

export const SearchInput = styled.input`
  ${uOutlineNone};
  flex: 1;
  height: 38px;
  padding: ${pxToRem(9)} ${pxToRem(19)} ${pxToRem(9)} ${pxToRem(16)};
  border: none;
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorInputBg};
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextPrimary};
`;

export const SearchClearButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  position: absolute;
  right: ${pxToRem(10)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${themeColors.colorTextSecondary};

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const SearchClearIcon = styled(AlertCloseIconSvg)``;

export const WeightSelector = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${pxToRem(8)};
`;

// Full-bleed divider between the weight selector and the product-selection
// instruction text. Negative horizontal margins cancel BasicModalBody's
// padding so the line spans the modal's full width, same technique as
// ScrollArea above.
export const ProductSelectionDivider = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: ${pxToRem(16)} ${pxToRem(-16)} ${pxToRem(12)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: ${pxToRemMd(16)} ${pxToRemMd(-16)} ${pxToRemMd(12)};
  }
`;

export const WeightLabel = styled.p`
  font-family: ${variables.family.goldSilver};
  margin: 0;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
`;

export const WeightField = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border: 1px solid ${themeColors.colorInputBorder};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorPrimary};
`;

export const WeightFieldChange = styled.span`
  font-family: ${variables.family.goldSilver};
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
  font-size: 12px;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
`;

export const WeightFieldArrow = styled(ArrowDownSvg)`
  &&& {
    width: 11px;
    height: 6px;
  }
`;

export const InstructionText = styled.p`
  font-family: ${variables.family.goldSilver};
  flex-shrink: 0;
  margin: 0 0 ${pxToRem(8)};
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
`;

// Single scroll region for the product list. As a flex:1 child of the modal
// body (a flex column) it takes the space between the fixed filter header and
// the fixed Continue footer, so the list scrolls here while the header and
// footer stay put — the header is a plain flex item (no sticky) so it never
// travels on scroll, and the footer stays pinned at the modal bottom.
// Negative margins cancel BasicModalBody's horizontal padding so the scrollbar
// sits at the modal edge; the List re-adds that padding so rows stay inset
// symmetrically and clear the scrollbar. (Clipped by the body's overflow:hidden,
// so the horizontal bleed never adds its own scrollbar.)
export const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 ${pxToRem(-16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: 0 ${pxToRemMd(-16)};
  }
`;

export const List = styled.ul`
  list-style: none;
  padding: 0 ${pxToRem(16)};
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: 0 ${pxToRemMd(16)};
  }
`;

// Empty product list. Mirrors the Price Alerts "Active Alerts" empty state
// (info icon + primary line + secondary line). height:100% so it fills the
// ScrollArea and centers vertically like the alerts-tab version.
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  text-align: center;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(24)} 0;
`;

export const EmptyStateIcon = styled(InfoGttIconSvg)`
  width: 44px;
  height: 44px;
  margin: 12px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: 8.25px;
  }
`;

export const EmptyStatePrimaryText = styled.p`
  font-size: 18px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextPrimary};
  margin: 0;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    font-family: ${variables.family.goldSilver};
  }
`;

export const EmptyStateSecondaryText = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  margin: 0;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    font-family: ${variables.family.goldSilver};
  }
`;

// The weight-drawer's checkbox grid: two columns of bordered cards.
export const WeightGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(12)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: ${pxToRem(8)};
  }
`;

export const WeightOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(16)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorBackgroundSecondary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  height: 42px;
  color: ${themeColors.colorTextPrimary};
`;

// Full-width action button, used for the weight-drawer "Apply" footer and the
// screen's "Continue" footer.
export const FullWidthApply = styled.div`
  flex-shrink: 0;

  && button {
    width: 100%;
    height: 44px;
    font-family: ${variables.family.goldSilver};
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
  }
`;

export const ModalCloseIcon = styled(AlertCloseIconSvg)`
  &&& {
    width: 12.5px;
    height: 12.5px;
  }
`;

export const SearchIcon = styled(SearchSvg)`
  &&& {
    width: 19.52px;
    height: 19.52px;
    color: #000000;
  }
`;

// Selectable product row: radio dot · thumbnail · (name, price) · weight.
export const ProductRow = styled.label`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto ${pxToRem(56)} 1fr auto;
  align-items: center;
  gap: ${pxToRem(10)};
  padding: ${pxToRem(8)} ${pxToRem(12)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorBackgroundSecondary};
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? themeColors.colorTextSecondary : 'transparent'};
  cursor: pointer;
`;

export const ProductRadio = styled.span`
  display: inline-flex;
`;

export const ProductThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: contain;
`;

export const ProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(4)};
  min-width: 0;
`;

export const ProductNameText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextPrimary};
`;

export const ProductPriceText = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 600;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
`;

export const ProductWeightText = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  white-space: nowrap;
`;

// --- Order-entry (TargetPriceOrderEntry) styled components ------------------

// Product name + live market price header row.
export const ProductHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  margin-bottom: ${pxToRem(24)};
`;

export const ProductName = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 130%;
  color: ${themeColors.colorTextPrimary};
`;

export const ProductPrice = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
`;

export const FieldLabel = styled.p`
  margin: 0 0 ${pxToRem(8)};
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};

  // GoldSilver/label/300, desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.label[300]}
  }
`;

// Bordered "$"-prefixed currency input for the target price.
export const PriceInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 54px;
  padding: 0 ${pxToRem(16)};
  background: ${themeColors.colorInputBg};
  border: 1px solid
    ${({ hasError }) =>
      hasError ? themeColors.colorError : themeColors.colorInputBg};
  border-radius: ${variables.borderRadius.borderRadius};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    height: 38px;
  }
`;

export const DollarSign = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
  margin-right: ${pxToRem(4)};

  // GoldSilver/label/400, desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.label[400]}
  }
`;

export const PriceInput = styled.input`
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  border: 0;
  background: ${themeColors.colorInputBg};
  font-size: ${pxToRem(18)};
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};

  ::placeholder {
    color: ${themeColors.colorTextSecondary};
    opacity: 0.6;
  }
`;

export const PriceError = styled.p`
  margin: ${pxToRem(6)} 0 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;
  color: ${themeColors.colorError};
`;

// Puts the target-price input (each with its own label above it) and the
// "below market" quick-fill pills side by side in a 60/40 split, as two
// columns of a single row — guaranteeing both labels and both content
// blocks line up, rather than keeping two separate rows in sync. Stacks to
// a column on narrow widths where both can't fit on one row.
export const PriceAndBelowMarketRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 3;
  min-width: 0;
`;

export const BelowMarketColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  min-width: 0;

  // "Recommended Selection" is dropped on mobile; the pills sit directly
  // under the price input with no label of their own.
  ${mediaBelow(variables.breakpoints.bpMd)} {
    ${FieldLabel} {
      display: none;
    }
  }
`;

// Vault-selection row wrapper. margin-top:auto pushes the vault row (and the
// footer below it) to the bottom of the flex-column modal body. position:
// relative gives BasicDivider (rendered as its first child) an anchor to pin
// itself to the wrapper's top edge, independent of the wrapper's own padding.
export const VaultSelectionWrapper = styled.div`
  position: relative;
  margin-top: auto;
  padding-top: ${pxToRem(16)};
`;

// Full-bleed horizontal rule, absolutely pinned to the top edge of a
// position:relative parent regardless of that parent's own box model
// (padding, content height, etc.). left/right bleed through BasicModalBody's
// 16px padding so the line spans the modal's full width.
export const BasicDivider = styled.div`
  position: absolute;
  top: 0;
  left: ${pxToRem(-16)};
  right: ${pxToRem(-16)};
  border-top: 1px solid ${themeColors.colorBorderPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    left: ${pxToRemMd(-16)};
    right: ${pxToRemMd(-16)};
  }
`;

// Overrides RowItemElement's default 16px colorTextPrimary label for the vault
// row: fixed 14px, secondary color. display:flex + height:100% re-centers the
// text within RowItemLabel's own box — at 14px (vs the 12px value/CTA text
// beside it) the browser's line-height/leading otherwise renders it slightly
// low relative to the rest of the row.
export const VaultLabelText = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};

  // GoldSilver/body/400, desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.body[400]}
  }
`;

// Vault value group: icon on the left, name, then the Best Price/Segregated
// banner. Centered by default so the icon and vault name line up with the
// row's other fields (label, clickLabel) in the common single-line case.
export const VaultValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
`;

export const VaultIcon = styled(VaultSvg)`
  &&& {
    width: 14.62px;
    height: 13.5px;
  }
`;

// Overrides RowItemElement's default 16px colorTextPrimary value color for the
// vault name / "select vault" placeholder: fixed 12px, secondary color.
export const VaultNameText = styled.span`
  white-space: nowrap;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};

  // gbi/ui font-size/300, desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    font-size: ${rem(typeSize[300])};
  }
`;

// Overrides RowItemElement's default 16px clickLabel styling for the vault
// row's "Select" CTA text.
export const SelectCtaText = styled.span`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};

  // GoldSilver/body/400, desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.body[400]}
  }
`;

// align-self: flex-start so that when the vault name wraps onto two lines at
// narrower widths, the banner stays anchored to the first line instead of
// being vertically centered across both (VaultValue centers by default now).
export const BestPriceBanner = styled.span`
  align-self: flex-start;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: ${themeColors.colorWhite};
  background-color: ${themeColors.colorSuccess};
  border-radius: ${variables.borderRadius.borderRadiusSm};
  padding: ${pxToRem(4)} ${pxToRem(6)};
  margin-left: ${pxToRem(6)};
`;

// Segregated Storage indicator — shared by the Order Entry vault row
// (replacing BestPriceBanner there once Segregated is selected; see
// isLowestVaultSelected in TargetPriceOrderEntry) and the Review screen's
// "Storing in X Vault" line. One declaration so a future styling tweak only
// has to happen once. align-self: center keeps it vertically centered
// against the vault name in VaultValue (which uses align-items: flex-start
// so a wrapped two-line vault name still anchors Best Price to its first
// line) — a harmless no-op in ReviewVaultRow, which already centers.
export const SegregatedTag = styled.span`
  align-self: center;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: ${themeColors.colorWhite};
  background-color: ${themeColors.colorButtonPrimary};
  border-radius: ${variables.borderRadius.borderRadiusSm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${pxToRem(4)} ${pxToRem(6)};
`;

// Footer row: "Buy at Current Price?" text link on the left, the primary
// "Review Target Order" button on the right. Normally sits directly below
// VaultSelectionWrapper's margin-top:auto with a fixed 17px gap — but for
// instavault products (no vault row, so nothing else carries the auto
// margin) pinToBottom shifts that margin-top:auto onto the footer itself, so
// it's still pushed to the bottom instead of floating right under the
// quantity block.
export const OrderEntryFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(16)};
  margin-top: ${({ pinToBottom }) => (pinToBottom ? 'auto' : pxToRem(17))};
`;

// Dims the order-entry screen while a deposit wizard is open over the flow.
// Those wizards run with `overModal`, which makes their own backdrop
// transparent (so the GTT modal behind isn't double-dimmed) — that leaves the
// form fully lit, hence this local dimmer. Mirrors Buy/OrderSelection's
// BuyModalDimmer. The large z-index is scoped to GTT's own stacking context,
// so it still sits below the root-portaled deposit modal.
export const AddFundsDimmer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 9999999;
`;

export const BuyAtCurrentPriceLink = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;

  span {
    font-family: ${variables.family.goldSilver};
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: 0;
    vertical-align: middle;
    color: ${themeColors.colorPrimary};
    text-decoration: none;
  }

  &:hover span {
    text-decoration: underline;
  }
`;

// Wrapper so the primary "Review Target Order" button flexes to fill the space
// left over by the "Buy at Current Price?" link.
export const ReviewButtonWrapper = styled.div`
  flex: 1;

  button {
    width: 100%;
  }
`;

// Review Target Order button: overrides the shared Button's disabled state
// (opacity: 0.5 dimming) with a solid disabled background, scoped to this
// button only — every other button in the app keeps the default dimming.
// The extra & bumps specificity above Button's own primary-variant
// `:disabled:hover` rule (which resets hover back to the enabled
// colorButtonPrimary), so the disabled background stays put on hover too.
export const ReviewTargetOrderButton = styled(Button)`
  &&:disabled {
    background: ${themeColors.colorButtonPrimaryDisabled};
    border-color: ${themeColors.colorButtonPrimaryDisabled};
    opacity: 1;

    &:hover,
    &:focus,
    &:active {
      background: ${themeColors.colorButtonPrimaryDisabled};
      border-color: ${themeColors.colorButtonPrimaryDisabled};
      box-shadow: none;
    }
  }
`;

// Quantity total + available-funds summary shown under the quantity stepper,
// mirroring the Buy flow's calculated-values block (see BuyInputFields).
export const QuantityCalcWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${pxToRem(16)};
`;

export const QuantityCalcTotal = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  color: ${themeColors.colorTextPrimary};
  margin: 0;

  b {
    font-weight: 700;
  }

  // GoldSilver/body/300 (and /title/300 for the amount), desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.body[300]}

    b {
      ${tokens.type.title[300]}
    }
  }
`;

export const QuantityCalcDivider = styled.div`
  width: 100%;
  max-width: ${pxToRem(300)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  margin: ${pxToRem(10)} 0;
`;

export const AvailableFundsRow = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0;
  text-align: center;
  color: ${themeColors.colorTextSecondary};
  margin: 0;

  b {
    font-weight: 500;
    color: ${themeColors.colorPrimary};
  }

  // GoldSilver/body/300 (and /label/300 for the amount), desktop only.
  ${mediaUp(variables.breakpoints.bpMd)} {
    ${tokens.type.body[300]}
    color: ${themeColors.colorTextSecondary};

    b {
      ${tokens.type.label[300]}
      color: ${themeColors.colorPrimary};
    }
  }
`;

// Shortfall line under Available Funds, shown only when the order would
// reserve more than the balance covers. Font size is literal px, not rem, so
// it stays 14px rather than tracking the root font-size.
export const AvailableFundsRequired = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0;
  text-align: center;
  color: ${themeColors.colorBalanceLow};
  margin: ${pxToRem(2)} 0 0;
`;

// Row of "below market" quick-fill pills.
export const BelowMarketRow = styled.div`
  display: flex;
  gap: ${pxToRem(8)};
  min-width: 0;
`;

export const BelowMarketButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(2)};
  height: 54px;
  padding: 0 ${pxToRem(8)};
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px solid
    ${({ isSelected }) => (isSelected ? SLATE_600 : 'transparent')};
  background-color: ${themeColors.colorBackgroundSecondary};
  cursor: pointer;
  box-shadow: none;
  transition: box-shadow 0.1s ease;

  // Fixed-size pill on mobile instead of splitting the row's full width,
  // so the two buttons sit left-aligned rather than stretching edge to edge.
  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex: 0 0 auto;
    min-width: 96px;
    height: 50px;
  }

  // Drop-shadow appears on mouse down and is removed on mouse up for a
  // tactile click effect (:active spans the press).
  &:active {
    box-shadow: 0 ${pxToRem(4)} ${pxToRem(8)} rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const BelowMarketPrice = styled.span`
  ${tokens.type.title[300]}
  color: ${themeColors.colorTextPrimary};
`;

export const BelowMarketLabel = styled.span`
  ${tokens.type.label[200]}
  color: ${tokens.color.status.success};
`;

// Quantity stepper: [−] value [+].
export const QuantityWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  height: 38px;
  padding: 0 ${pxToRem(8)};
  border-radius: ${variables.borderRadius.borderRadius};
  background: ${themeColors.colorInputBg};

  ${mediaUp(variables.breakpoints.bpMd)} {
    height: 50px;
  }
`;

export const StepperButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px solid ${themeColors.colorCardBorder};
  background-color: ${themeColors.colorBackgroundSecondary};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// The source SVGs are a 28×28 canvas with the glyph centered, so filling the
// 24px button draws the plus glyph at ~14×14 and the minus bar at ~14×2.
// Sizing the element itself to 14×2/14×14 would letterbox the whole canvas and
// shrink the glyph to nothing. Recolor the path to colorTextPrimary.
export const StepperPlusIcon = styled(PlusIconSvg)`
  &&& {
    width: 24px;
    height: 24px;

    path {
      fill: ${themeColors.colorTextPrimary};
    }
  }
`;

export const StepperMinusIcon = styled(MinusIconSvg)`
  &&& {
    width: 24px;
    height: 24px;

    path {
      fill: ${themeColors.colorTextPrimary};
    }
  }
`;

// --- Order-review (TargetPriceOrderReview) styled components ---------------

export const ReviewProductRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(16)};
  margin-bottom: ${pxToRem(16)};
`;

export const ReviewThumb = styled.img`
  width: 61px;
  height: 61px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 30.5px;
`;

export const ReviewProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  min-width: 0;
`;

export const ReviewOrderLabel = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 600;
    font-size: 18px;
  }
`;

export const ReviewProductName = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 600;
  line-height: 100%;
  color: ${themeColors.colorTextPrimary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 600;
    font-size: 18px;
  }
`;

export const ReviewVaultText = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: ${({ fontWeightMobile }) => fontWeightMobile || 400};
    font-size: ${({ fontSizeMobile }) => fontSizeMobile || 14}px;
  }
`;

// The vault name + "Vault" portion of ReviewVaultText's "Storing in {vault}
// Vault" sentence, set apart with a heavier weight. Mirrors ReviewVaultText's
// own breakpoint sizing so both segments of the sentence stay the same size.
export const ReviewVaultName = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: ${({ fontWeightMobile }) => fontWeightMobile || 500};
    font-size: ${({ fontSizeMobile }) => fontSizeMobile || 14}px;
  }
`;

// Shared "Storing in {vault} Vault" line used by both TargetPriceOrderReview
// and TargetPriceOrderConfirmation. Splits the translated sentence around
// the vault name so just that segment (+ "Vault") can render in
// ReviewVaultName's heavier weight, without hardcoding the surrounding copy.
// fontSizeMobile/fontWeightMobile let a single caller (TargetPriceOrderReview)
// override the mobile sizing without affecting the other caller.
export const StoringInVaultText = ({
  translationKey,
  vaultName,
  fontSizeMobile,
  fontWeightMobile,
}) => {
  const { t } = useTranslation();
  const storingInText = t(translationKey, { vault: vaultName });
  const [storingInPrefix, storingInSuffix] = vaultName
    ? storingInText.split(vaultName)
    : [storingInText, ''];

  return (
    <ReviewVaultText
      fontSizeMobile={fontSizeMobile}
      fontWeightMobile={fontWeightMobile}
    >
      {storingInPrefix}
      {vaultName && (
        <ReviewVaultName
          fontSizeMobile={fontSizeMobile}
          fontWeightMobile={fontWeightMobile}
        >
          {vaultName}
          {storingInSuffix}
        </ReviewVaultName>
      )}
    </ReviewVaultText>
  );
};

StoringInVaultText.propTypes = {
  translationKey: PropTypes.string.isRequired,
  vaultName: PropTypes.string,
  fontSizeMobile: PropTypes.number,
  fontWeightMobile: PropTypes.number,
};

StoringInVaultText.defaultProps = {
  vaultName: '',
  fontSizeMobile: undefined,
  fontWeightMobile: undefined,
};

// Wraps StoringInVaultText + SegregatedTag so the segregated-storage badge
// sits inline with (and wraps under, on narrow widths) the vault sentence
// instead of stacking as its own block.
export const ReviewVaultRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(6)};
`;

// Full-width horizontal rule between review sections (unlike
// QuantityCalcDivider, which is capped to 300px for the centered quantity
// summary block).
// fullBleed: bleeds past BasicModalBody's 16px padding to span the modal's
// full width, same negative-margin technique as ProductSelectionDivider.
export const ReviewDivider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  margin: ${pxToRem(16)} 0;

  ${({ fullBleed }) =>
    fullBleed &&
    `
    margin: ${pxToRem(16)} ${pxToRem(-16)};
    width: auto;

    ${mediaBelow(variables.breakpoints.bpMd)} {
      margin: ${pxToRemMd(16)} ${pxToRemMd(-16)};
    }
  `}
`;

// Secondary single-line caption under the Subtotal / Total rows (e.g.
// "Current Price ($4,245.20)" / "Target Price ($4,032.75x12)") — not a
// label/value pair, so it isn't an InformationListItem.
export const PriceCaption = styled.p`
  font-family: ${variables.family.goldSilver};
  margin: ${pxToRem(-4)} 0 ${pxToRem(12)};
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

// Pushes the terms checkbox + submit button to the bottom of the flex-column
// modal body, mirroring VaultSelectionWrapper's margin-top:auto in the
// order-entry screen (ModalButtons' own built-in `margin-top: auto` gets
// overridden by its `marginTop` prop, so the push has to happen here instead).
export const ReviewFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(16)};
  margin-top: auto;
`;

// Reserved-funds info banner. Left-aligned icon + multi-line text (unlike
// Buy/OrderConfirmation's centered single-line InfoBanner).
export const ReservedFundsBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorBackgroundSecondary};
  color: ${themeColors.colorTextSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  min-height: 72px;

  svg {
    flex-shrink: 0;
    margin-top: ${pxToRem(2)};
  }
`;

// ReservedFundsBanner's text. Paragraph has no breakpoint awareness of its
// own, so the mobile bump to 16px is added here.
export const ReservedFundsText = styled(Paragraph)`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

// Volume-discount info banner. Bordered success-tinted callout (unlike
// ReservedFundsBanner's neutral filled background) since the discount is
// contingent on execution rather than a settled figure.
export const VolumeDiscountBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  margin-bottom: ${pxToRem(16)};
  border: 1px solid ${themeColors.colorBadgeSuccess};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorGreenDarkOpacity};
  color: ${themeColors.colorBestFlag};

  svg {
    flex-shrink: 0;
    margin-top: ${pxToRem(2)};
  }
`;

export const VolumeDiscountIcon = styled(DiscountSvg)`
  &&& {
    width: 15.75px;
    height: 15.75px;
  }
`;

// Terms checkbox row — mirrors PriceAlerts' AlertEntry SMS-consent row
// (CheckboxGTT + inline text/link) rather than the generic AgreementCheckbox
// used elsewhere, for visual parity between the two GTT flows.
export const ReviewTermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
`;

export const ReviewTermsText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  text-align: left;
  overflow-wrap: break-word;
`;

export const ReviewTermsLink = styled.span`
  color: ${themeColors.colorLinkPrimary};
  cursor: pointer;
  text-decoration: underline;
`;

// --- Order-confirmation (TargetPriceOrderConfirmation) styled components ----

// setWizardTitle('') collapses BasicModal's own header on this last step, so
// the screen draws its own close affordance here instead (mirrors
// PriceAlerts' AlertConfirmation Container/CloseButton).
export const ConfirmationContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${pxToRem(16)};
  background-color: ${themeColors.colorBackgroundPrimary};
`;

export const ConfirmationCloseButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  position: absolute;
  top: ${pxToRem(16)};
  right: ${pxToRem(16)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Pushes the savings banner + notify text + button to the bottom of the
// flex-column screen, same margin-top:auto trick as ReviewFooterWrapper.
export const ConfirmationFooterWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
`;

export const ConfirmationProductImage = styled.img`
  width: 183px;
  height: 183px;
  object-fit: contain;
  border-radius: 50%;
  margin: ${pxToRem(16)} 0;
`;

export const ConfirmationTitle = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 20px;
  line-height: 130%;
  margin-bottom: ${pxToRem(12)};
  color: ${themeColors.colorTextPrimary};
`;

export const ConfirmationProductLine = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 700;
  font-size: 20px;
  line-height: 120%;
  margin-bottom: ${pxToRem(2)};
  color: ${themeColors.colorIconTertiary};
`;

export const ConfirmationSubText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  margin-bottom: ${pxToRem(12)};
  color: ${themeColors.colorTextSecondary};

  b {
    font-weight: 700;
  }
`;

// Green "you'd save" banner — single-line icon + text + bold amount, unlike
// ReservedFundsBanner's neutral multi-line layout. Palette matches the
// savings figure already used in TargetPriceOrderReview (colorBestFlag).
export const SavingsBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  width: 100%;
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border-radius: ${variables.borderRadius.borderRadius};
  background-color: ${themeColors.colorGreenDarkOpacity};
  color: ${themeColors.colorBestFlag};
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: ${pxToRem(16)};
  border: 1px solid ${themeColors.colorBestFlag};

  svg {
    flex-shrink: 0;
  }

  b {
    margin-left: auto;
    font-weight: 700;
  }
`;

export const SavingsBannerIcon = styled(HandCoinsSvg)`
  width: 20px;
  height: 20px;
`;

export const ConfirmationNotifyText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-style: italic;
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  margin: 0 0 ${pxToRem(16)};
  color: ${themeColors.colorTextSecondary};
`;

export const ConfirmationButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  button {
    height: 44px;
    padding: 0 ${pxToRem(16)};
    font-size: 16px;
  }
`;

export const QuantityValue = styled.input`
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  color: ${themeColors.colorTextPrimary};

  // Hide the native number-input spinners; the +/- buttons replace them.
  appearance: textfield;
  -moz-appearance: textfield;

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
